/**
 * The KDF worker on the page (decision 25; part 25a log, table H): each derivation starts a worker of its own and ends it
 * once answered, whatever the answer, so Argon2id's memory (up to the ceiling) and the password's copies go with it.
 * Its first message, error or unreadable message settles the derivation; anything after is ignored. No time limit:
 * Argon2id always ends, bounded by the ceiling, and the page decides what the writer sees meanwhile. The worker is
 * injected, so the logic runs in tests. Part 41b: the backup lock's key and a locked backup's opening run the same way.
 */
import type { BackupFailure, BackupKey, KdfRecord } from '@cockatrice/crypto';
import { readAnswer, readBackupAnswer } from './protocol.ts';
import type { BackupKdfAnswer, BackupKdfJob, KdfAnswer, KdfJob } from './protocol.ts';

/** A KDF worker, as far as the page drives it (a `Worker`). */
export interface KdfWorker {
  addEventListener(type: 'message' | 'messageerror' | 'error', listener: (event: unknown) => void): void;
  postMessage(message: KdfJob | BackupKdfJob): void;
  terminate(): void;
}

/** A message event's data; undefined for anything else. */
function dataOf(event: unknown): unknown {
  return typeof event === 'object' && event !== null ? Reflect.get(event, 'data') : undefined;
}

/**
 * H1–H7: runs `job` in a worker from `start` and ends it. The worker's answer (M2–M4), or `failed` when the worker
 * can't start (H2) or be sent the job (H3), reports an error (H4, H5) or answers what doesn't read (H6). Never rejects.
 */
export function deriveInWorker(start: () => KdfWorker, job: KdfJob): Promise<KdfAnswer> {
  return inWorker(start, job, readAnswer);
}

/** H1–H7 for any job: `read` reads the answer; `failed` for every way it goes wrong. Never rejects. */
function inWorker<A>(start: () => KdfWorker, job: KdfJob | BackupKdfJob, read: (data: unknown) => A | null): Promise<A | { readonly kind: 'failed' }> {
  return new Promise((resolve) => {
    let worker: KdfWorker;
    try {
      worker = start();
    } catch {
      resolve({ kind: 'failed' });
      return;
    }
    let settled = false;
    const settle = (answer: A | { readonly kind: 'failed' }): void => {
      if (settled) return;
      settled = true;
      worker.terminate();
      resolve(answer);
    };
    worker.addEventListener('message', (event) => {
      settle(read(dataOf(event)) ?? { kind: 'failed' });
    });
    for (const type of ['error', 'messageerror'] as const) {
      worker.addEventListener(type, () => {
        settle({ kind: 'failed' });
      });
    }
    try {
      worker.postMessage(job);
    } catch {
      settle({ kind: 'failed' });
    }
  });
}

/** Part 41b, BP2: a backup key for a passphrase and its check value, or a failure (the worker's, or a passphrase it can't hash). */
export type BackupKeyResult = { readonly kind: 'derived'; readonly key: BackupKey; readonly check: Uint8Array<ArrayBuffer> } | { readonly kind: 'failed' };

/** Part 41b, E11–E13: a locked backup's plaintext bundle, or why it wasn't opened (a refusal, or `failed` for anything else). */
export type OpenedBackup = { readonly kind: 'opened'; readonly file: Blob } | { readonly kind: 'refused'; readonly reason: BackupFailure | 'failed' };

/** Part 41b: the backup lock's work in the KDF worker, a worker of its own for each call (table H). */
export interface BackupKdf {
  /**
   * BP2, E15, E16: the backup key for `passphrase` under `kdf` (a check of the lock set), or under a new KDF record when
   * null (set or changed: a fresh salt), with its check value. Never rejects.
   */
  readonly backupKey: (passphrase: string, kdf: KdfRecord | null) => Promise<BackupKeyResult>;
  /** E11–E13: the locked backup `file` opened with `passphrase`, or its refusal. Never rejects. */
  readonly openBackup: (file: Blob, passphrase: string) => Promise<OpenedBackup>;
}

/** Part 41b: the backup lock's KDF work, each call in a worker from `start`, ended once answered (table H). */
export function backupKdf(start: () => KdfWorker): BackupKdf {
  return {
    backupKey: async (passphrase, kdf) => {
      const answer: BackupKdfAnswer = await inWorker(start, { kind: 'backup-key', passphrase, kdf }, readBackupAnswer);
      return answer.kind === 'backup-key' ? { kind: 'derived', key: answer.key, check: answer.check } : { kind: 'failed' };
    },
    openBackup: async (file, passphrase) => {
      const answer: BackupKdfAnswer = await inWorker(start, { kind: 'open-backup', file, passphrase }, readBackupAnswer);
      if (answer.kind === 'opened') return { kind: 'opened', file: answer.file };
      return { kind: 'refused', reason: answer.kind === 'backup-refused' ? answer.reason : 'failed' };
    },
  };
}
